import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  Chip,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useGetReportQuery } from '../../../store/api/reportsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetProductsQuery, useGetCategoriesQuery } from '../../../store/api/productsApi';
import { useGetAccountsQuery } from '../../../store/api/financeApi';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
import { useGetSuppliersQuery } from '../../../store/api/purchaseApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { getLocalDateString } from '../../../shared/utils/dateUtils';
import { getReportById } from '../config/reportCatalog';
import { exportReportToPdf, exportReportToExcel } from '../utils/reportExport';

function ReportView() {
  const { reportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const paramCustomerId = searchParams.get('customerId');
  const paramSupplierId = searchParams.get('supplierId');

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return getLocalDateString(d);
  });
  const [toDate, setToDate] = useState(() => getLocalDateString());
  const [productId, setProductId] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [categoryId, setCategoryId] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [accountId, setAccountId] = useState(null);
  const [customerId, setCustomerId] = useState(() => paramCustomerId || null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [supplierId, setSupplierId] = useState(() => paramSupplierId || null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [viewType, setViewType] = useState(() => (reportId === 'shift-wise-summary' ? 'summary' : 'detailed'));
  const [salesMode, setSalesMode] = useState('ALL');
  const [salesPersonId, setSalesPersonId] = useState('ALL');
  const [activityAction, setActivityAction] = useState('ALL');
  const [activityResource, setActivityResource] = useState('ALL');
  const [activityUserId, setActivityUserId] = useState('ALL');

  // Reset when changing reports
  useEffect(() => {
    if (
      reportId === 'day-book' ||
      reportId === 'daily-sales' ||
      reportId === 'shift-wise-sales' ||
      reportId === 'shift-wise-summary'
    ) {
      const today = getLocalDateString();
      setFromDate(today);
      setToDate(today);
    } else {
      const d = new Date();
      d.setDate(1);
      setFromDate(getLocalDateString(d));
      setToDate(getLocalDateString());
    }
    setProductId(null);
    setProductSearch('');
    setCategoryId('ALL');
    setStatusFilter('ALL');
    setAccountId(null);
    setCustomerId(paramCustomerId || null);
    setCustomerSearch('');
    setSupplierId(paramSupplierId || null);
    setSupplierSearch('');
    setViewType(reportId === 'shift-wise-summary' ? 'summary' : 'detailed');
    setSalesMode('ALL');
    setSalesPersonId('ALL');
    setActivityAction('ALL');
    setActivityResource('ALL');
    setActivityUserId('ALL');
  }, [reportId, paramCustomerId, paramSupplierId]);

  const { data: productsData, isFetching: isProductsLoading } = useGetProductsQuery(
    { search: productSearch, limit: 100 },
    { skip: reportId !== 'product-item-report' }
  );
  const products = productsData?.products || [];

  const { data: categoriesData, isFetching: isCategoriesLoading } = useGetCategoriesQuery(
    undefined,
    { skip: reportId !== 'category-wise-stock' && reportId !== 'product-sales-report' && reportId !== 'item-wise-profit' }
  );

  const categories = categoriesData ? [{ id: 'ALL', name: 'All Categories' }, ...categoriesData] : [{ id: 'ALL', name: 'All Categories' }];

  const { data: accountsData = [], isFetching: isAccountsLoading } = useGetAccountsQuery(undefined, { skip: reportId !== 'account-statement' });

  const { data: customersData, isFetching: isCustomersLoading } = useGetCustomersQuery(
    { search: customerSearch, limit: 1000 },
    { skip: reportId !== 'customer-ledger' && reportId !== 'customer-wise-sales' }
  );
  const customersList = customersData?.customers || (Array.isArray(customersData) ? customersData : []);

  const { data: suppliersData, isFetching: isSuppliersLoading } = useGetSuppliersQuery(
    { search: supplierSearch, limit: 1000 },
    { skip: reportId !== 'supplier-ledger' && reportId !== 'supplier-wise-purchase' }
  );
  const suppliersList = suppliersData?.suppliers || (Array.isArray(suppliersData) ? suppliersData : []);

  const reportMeta = getReportById(reportId);
  const isSalesReport =
    reportMeta?.categoryId === 'sales' ||
    reportId === 'sales-summary' ||
    reportId === 'sales-detailed' ||
    reportId === 'customer-wise-sales' ||
    reportId === 'shift-wise-sales' ||
    reportId === 'shift-wise-summary' ||
    reportId === 'sales-by-salesperson' ||
    reportId === 'daily-sales';

  const { data: usersData, isFetching: isUsersLoading } = useGetUsersQuery(undefined, {
    skip: !isSalesReport && reportId !== 'activity-log-report',
  });
  const usersList = Array.isArray(usersData) ? usersData : usersData?.users || [];

  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data, isLoading, error } = useGetReportQuery(
    {
      reportType: reportId,
      fromDate,
      toDate,
      productId,
      categoryId: categoryId === 'ALL' ? undefined : categoryId,
      statusFilter,
      accountId,
      customerId: customerId || undefined,
      supplierId: supplierId || undefined,
      viewType,
      salesType: salesMode === 'ALL' ? undefined : salesMode,
      salesPersonId: salesPersonId === 'ALL' ? undefined : salesPersonId,
      userId: reportId === 'activity-log-report' ? (activityUserId === 'ALL' ? undefined : activityUserId) : undefined,
      action: reportId === 'activity-log-report' ? (activityAction === 'ALL' ? undefined : activityAction) : undefined,
      resource: reportId === 'activity-log-report' ? (activityResource === 'ALL' ? undefined : activityResource) : undefined,
    },
    {
      skip:
        !reportId ||
        (reportId === 'product-item-report' && !productId) ||
        (reportId === 'account-statement' && !accountId),
    }
  );

  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '–';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) }).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '–');
  const formatDateTime = (v) => {
    if (!v) return '–';
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      return d.toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return String(v);
    }
  };

  let periodText = `${formatDate(fromDate)} – ${formatDate(toDate)}`;
  if (salesMode && salesMode !== 'ALL') {
    periodText += ` | Mode: ${salesMode.toUpperCase()}`;
  }
  if (salesPersonId && salesPersonId !== 'ALL') {
    const sp = usersList.find((u) => u.id === salesPersonId);
    const spName = sp ? `${sp.firstName || ''} ${sp.lastName || ''}`.trim() || sp.email : 'Selected';
    periodText += ` | Salesperson: ${spName}`;
  }
  const safeFilename = (reportId || 'report').replace(/[^a-z0-9-_]/gi, '_');

  const handleExportPdf = () => {
    if (!data) return;
    exportReportToPdf({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
    });
  };

  const handleExportExcel = () => {
    if (!data) return;
    exportReportToExcel({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
      sheetName: (reportMeta?.label || 'Report').slice(0, 31),
    });
  };

  const handleBack = () => {
    // Prefer history back so we return to category page like /reports/category/inventory
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/reports');
  };

  if (!reportMeta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Report not found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ borderRadius: 2 }}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  const isInventoryReport =
    reportId === 'product-item-report' ||
    reportId === 'stock-ledger-summary' ||
    reportId === 'stock-audit-report' ||
    reportId === 'stock-item-list' ||
    reportId === 'category-wise-stock';

  const summaryItems = data?.summary
    ? [
      typeof data.summary.totalValue === 'number' && {
        label: 'Total Value',
        value: formatAmount(data.summary.totalValue),
      },
      typeof data.summary.totalSales === 'number' && {
        label: reportId === 'product-item-report' ? 'Total Sales' : 'Total Sales',
        value: isInventoryReport ? data.summary.totalSales.toLocaleString() : formatAmount(data.summary.totalSales),
      },
      typeof data.summary.totalCashSales === 'number' && {
        label: 'Cash Sales',
        value: formatAmount(data.summary.totalCashSales),
      },
      typeof data.summary.totalCreditSales === 'number' && {
        label: 'Credit Sales',
        value: formatAmount(data.summary.totalCreditSales),
      },
      typeof data.summary.receiptCash === 'number' && {
        label: 'Cash Receipts',
        value: formatAmount(data.summary.receiptCash),
      },
      typeof data.summary.receiptCard === 'number' && {
        label: 'Card Receipts',
        value: formatAmount(data.summary.receiptCard),
      },
      typeof data.summary.receiptBank === 'number' && {
        label: 'Bank Receipts',
        value: formatAmount(data.summary.receiptBank),
      },
      typeof data.summary.totalCardSales === 'number' && {
        label: 'Card Sales',
        value: formatAmount(data.summary.totalCardSales),
      },
      typeof data.summary.totalBankSales === 'number' && {
        label: 'Bank Sales',
        value: formatAmount(data.summary.totalBankSales),
      },
      typeof data.summary.totalExpectedCash === 'number' && {
        label: 'Expected Cash',
        value: formatAmount(data.summary.totalExpectedCash),
      },
      typeof data.summary.totalActualCash === 'number' && {
        label: 'Closing Cash',
        value: formatAmount(data.summary.totalActualCash),
      },
      typeof data.summary.totalVariance === 'number' && {
        label: 'Cash Variance',
        value: formatAmount(data.summary.totalVariance),
      },
      data.summary.totalShifts != null && {
        label: 'Total Shifts',
        value: data.summary.totalShifts.toLocaleString(),
      },
      typeof data.summary.receiptCheque === 'number' && {
        label: 'Cheque Receipts',
        value: formatAmount(data.summary.receiptCheque),
      },
      typeof data.summary.totalPurchase === 'number' && {
        label: 'Total Purchase',
        value: formatAmount(data.summary.totalPurchase),
      },
      typeof data.summary.totalExpense === 'number' && {
        label: 'Total Expense',
        value: formatAmount(data.summary.totalExpense),
      },
      typeof data.summary.totalOutstanding === 'number' && {
        label: 'Outstanding',
        value: formatAmount(data.summary.totalOutstanding),
      },
      typeof data.summary.totalBalance === 'number' && {
        label: 'Total Balance',
        value: formatAmount(data.summary.totalBalance),
      },
      typeof data.summary.totalDebit === 'number' && {
        label: 'Total Debit',
        value: formatAmount(data.summary.totalDebit),
      },
      typeof data.summary.totalCredit === 'number' && {
        label: 'Total Credit',
        value: formatAmount(data.summary.totalCredit),
      },
      typeof data.summary.openingBalance === 'number' && {
        label: 'Opening Balance',
        value: formatAmount(data.summary.openingBalance),
      },
      typeof data.summary.totalInflow === 'number' && {
        label: 'Total Inflow (Dr)',
        value: formatAmount(data.summary.totalInflow),
      },
      typeof data.summary.totalOutflow === 'number' && {
        label: 'Total Outflow (Cr)',
        value: formatAmount(data.summary.totalOutflow),
      },
      typeof data.summary.closingBalance === 'number' && {
        label: 'Closing Balance',
        value: isInventoryReport ? data.summary.closingBalance.toLocaleString() : formatAmount(data.summary.closingBalance),
      },
      typeof data.summary.totalTaxable === 'number' && {
        label: 'Total Taxable',
        value: formatAmount(data.summary.totalTaxable),
      },
      typeof data.summary.totalInputVat === 'number' && {
        label: 'Total Input VAT',
        value: formatAmount(data.summary.totalInputVat),
      },
      typeof data.summary.totalOutputVat === 'number' && {
        label: 'Total Output VAT',
        value: formatAmount(data.summary.totalOutputVat),
      },
      typeof data.summary.totalReturns === 'number' && {
        label: 'Total Returns',
        value: formatAmount(data.summary.totalReturns),
      },
      typeof data.summary.totalNetPurchase === 'number' && {
        label: 'Net Purchase',
        value: formatAmount(data.summary.totalNetPurchase),
      },
      typeof data.summary.totalPaid === 'number' && {
        label: 'Total Paid',
        value: formatAmount(data.summary.totalPaid),
      },
      typeof data.summary.totalGross === 'number' && {
        label: 'Gross Sales',
        value: formatAmount(data.summary.totalGross),
      },
      typeof data.summary.totalRevenue === 'number' && {
        label: 'Total Revenue',
        value: formatAmount(data.summary.totalRevenue),
      },
      typeof data.summary.totalCost === 'number' && {
        label: 'Total Cost',
        value: formatAmount(data.summary.totalCost),
      },
      typeof data.summary.totalProfit === 'number' && {
        label: 'Total Profit',
        value: formatAmount(data.summary.totalProfit),
      },
      typeof data.summary.totalMargin === 'number' && {
        label: 'Overall Margin',
        value: `${Number(data.summary.totalMargin).toFixed(1)}%`,
      },
      data.summary.profitableCount != null && {
        label: 'Profitable Bills',
        value: data.summary.profitableCount.toLocaleString(),
      },
      data.summary.lossCount != null && {
        label: 'Loss-Making Bills',
        value: data.summary.lossCount.toLocaleString(),
      },
      typeof data.summary.netVat === 'number' && {
        label: 'Net VAT (Payable / Receivable)',
        value: formatAmount(data.summary.netVat),
      },
      typeof data.summary.totalDiscount === 'number' && {
        label: 'Total Discount',
        value: formatAmount(data.summary.totalDiscount),
      },
      data.summary.ordersWithDiscount != null && {
        label: 'Orders with Discount',
        value: data.summary.ordersWithDiscount.toLocaleString(),
      },
      data.summary.totalActivities != null && {
        label: 'Total Activities',
        value: data.summary.totalActivities.toLocaleString(),
      },
      data.summary.activeUsers != null && {
        label: 'Active Users',
        value: data.summary.activeUsers.toLocaleString(),
      },
      data.summary.totalLogins != null && {
        label: 'Logins',
        value: data.summary.totalLogins.toLocaleString(),
      },
      data.summary.totalCreates != null && {
        label: 'Created Records',
        value: data.summary.totalCreates.toLocaleString(),
      },
      data.summary.totalUpdates != null && {
        label: 'Updated Records',
        value: data.summary.totalUpdates.toLocaleString(),
      },
      data.summary.totalDeletes != null && {
        label: 'Deleted Records',
        value: data.summary.totalDeletes.toLocaleString(),
      },
      data.summary.totalRows != null && {
        label: reportId === 'out-of-stock' ? 'Out of stock items' : (reportId === 'activity-log-report' ? 'Total Logs' : 'Records'),
        value: data.summary.totalRows.toLocaleString(),
      },
      data.summary.totalOrders != null && {
        label: 'Total Orders',
        value: data.summary.totalOrders.toLocaleString(),
      },
      data.summary.totalCategories != null && {
        label: 'Total Categories',
        value: data.summary.totalCategories.toLocaleString(),
      },
      data.summary.totalMatched != null && {
        label: 'Matched Products',
        value: data.summary.totalMatched.toLocaleString(),
      },
      data.summary.totalDiscrepancies != null && {
        label: 'Total Discrepancies',
        value: data.summary.totalDiscrepancies.toLocaleString(),
      },
      data.summary.totalPositive != null && {
        label: 'Positive Discrepancies',
        value: data.summary.totalPositive.toLocaleString(),
      },
      data.summary.totalNegative != null && {
        label: 'Negative Discrepancies',
        value: data.summary.totalNegative.toLocaleString(),
      },
      data.summary.totalMissing != null && {
        label: 'Missing Invoices',
        value: data.summary.totalMissing.toLocaleString(),
      },
      data.summary.slowMovingCount != null && {
        label: 'Slow Moving',
        value: data.summary.slowMovingCount.toLocaleString(),
      },
      data.summary.fastMovingCount != null && {
        label: 'Fast Moving',
        value: data.summary.fastMovingCount.toLocaleString(),
      },
      data.summary.totalPurchases != null && {
        label: 'Total Purchases',
        value: data.summary.totalPurchases.toLocaleString(),
      },
      data.summary.totalQuantity != null && {
        label: 'Total Qty Sold',
        value: data.summary.totalQuantity.toLocaleString(),
      },
      data.summary.initialBalance != null && {
        label: 'Initial Balance',
        value: data.summary.initialBalance.toLocaleString(),
      },
      data.summary.totalSalesReturns != null && {
        label: 'Sales Returns',
        value: data.summary.totalSalesReturns.toLocaleString(),
      },
      data.summary.totalPurchaseReturns != null && {
        label: 'Purchase Returns',
        value: data.summary.totalPurchaseReturns.toLocaleString(),
      },
      data.summary.totalDamages != null && {
        label: 'Damages',
        value: data.summary.totalDamages.toLocaleString(),
      },
      data.summary.currentStock != null && {
        label: 'Current Total Stock',
        value: data.summary.currentStock.toLocaleString(),
      },
      data.summary.expectedStock != null && {
        label: 'Expected Stock (Calculated)',
        value: data.summary.expectedStock.toLocaleString(),
      },
      data.summary.discrepancy != null && {
        label: 'Discrepancy',
        value: data.summary.discrepancy.toLocaleString(),
      },
      data.summary.auditStatus != null && {
        label: 'Audit Status',
        value: data.summary.auditStatus,
      },
      typeof data.summary.totalClosing === 'number' && {
        label: 'Total Closing Stock',
        value: data.summary.totalClosing,
      },
      typeof data.summary.totalStock === 'number' && {
        label: 'Total Stock',
        value: data.summary.totalStock.toLocaleString(),
      },
    ].filter(Boolean)
    : [];

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        '@media print': { bgcolor: 'transparent', p: 0, '& .no-print': { display: 'none' } },
      }}
    >
      {/* Header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' },
            }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>
              {reportMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card
        className="no-print"
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CalendarMonthOutlinedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={reportId === 'day-book' ? "Date" : "From"}
                value={new Date(fromDate)}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    const year = newValue.getFullYear();
                    const month = String(newValue.getMonth() + 1).padStart(2, '0');
                    const day = String(newValue.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    setFromDate(dateStr);
                    if (reportId === 'day-book') {
                      setToDate(dateStr);
                    }
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 168 } } } }}
                format="dd/MM/yyyy"
              />
              {reportId !== 'day-book' && (
                <DatePicker
                  label="To"
                  value={new Date(toDate)}
                  onChange={(newValue) => {
                    if (newValue && !isNaN(newValue.getTime())) {
                      const year = newValue.getFullYear();
                      const month = String(newValue.getMonth() + 1).padStart(2, '0');
                      const day = String(newValue.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      setToDate(dateStr);
                    }
                  }}
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 168 } } } }}
                  format="dd/MM/yyyy"
                />
              )}
            </LocalizationProvider>
            {reportId === 'product-item-report' && (
              <Autocomplete
                size="small"
                options={products}
                loading={isProductsLoading}
                getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                value={products.find(p => p.id === productId) || null}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    // Basic debounce to avoid too many requests
                    clearTimeout(window.searchTimeout);
                    window.searchTimeout = setTimeout(() => {
                      setProductSearch(newInputValue);
                    }, 300);
                  }
                }}
                onChange={(event, newValue) => {
                  setProductId(newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Product (SKU, Name, Brand...)"
                    sx={{ width: { xs: '100%', sm: 300, md: 350 } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isProductsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                filterOptions={(x) => x} // Disable client-side filtering since we do it on server
              />
            )}
            {reportId === 'account-statement' && (
              <Autocomplete
                size="small"
                options={accountsData}
                loading={isAccountsLoading}
                getOptionLabel={(option) => `${option.code} - ${option.name} (${option.type})`}
                value={accountsData.find(a => a.id === accountId) || null}
                onChange={(event, newValue) => {
                  setAccountId(newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Account"
                    sx={{ width: { xs: '100%', sm: 280, md: 300 } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isAccountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {(reportId === 'category-wise-stock' || reportId === 'product-sales-report' || reportId === 'item-wise-profit') && (
              <Autocomplete
                size="small"
                options={categories}
                loading={isCategoriesLoading}
                getOptionLabel={(option) => option.name}
                value={categories.find(c => c.id === categoryId) || categories[0]}
                onChange={(event, newValue) => {
                  setCategoryId(newValue ? newValue.id : 'ALL');
                }}
                disableClearable
                id={'auto-complete-category'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Category"
                    sx={{ width: { xs: '100%', sm: 220, md: 250 } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isCategoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {reportId === 'stock-audit-report' && (
              <TextField
                select
                size="small"
                label="Audit Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 180 } }}
              >
                <option value="ALL">All Statuses</option>
                <option value="DISCREPANCY">All Discrepancies</option>
                <option value="POSITIVE">Positive Discrepancy</option>
                <option value="NEGATIVE">Negative Discrepancy</option>
                <option value="MATCHED">Matched</option>
              </TextField>
            )}
            {reportId === 'stock-item-list' && (
              <TextField
                select
                size="small"
                label="Stock Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 180 } }}
              >
                <option value="ALL">All Items</option>
                <option value="INSTOCK">In Stock</option>
                <option value="OUTOFSTOCK">Out of Stock</option>
                <option value="NEGATIVE">Negative Stock</option>
              </TextField>
            )}
            {reportId === 'credit-sales' && (
              <TextField
                select
                size="small"
                label="Bill Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 180 } }}
              >
                <option value="ALL">All Bills</option>
                <option value="UNPAID">Unpaid Bills</option>
                <option value="PAID">Paid Bills</option>
              </TextField>
            )}
            {(reportId === 'shift-wise-sales' || reportId === 'shift-wise-summary') && (
              <>
                <TextField
                  select
                  size="small"
                  label="Report View"
                  value={viewType}
                  onChange={(e) => setViewType(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: '100%', sm: 220 } }}
                >
                  <option value="detailed">All Sales (Invoice-wise)</option>
                  <option value="summary">Shift Summary (Sessions)</option>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Shift Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                >
                  <option value="ALL">All Shifts</option>
                  <option value="open">Active / Open Shifts</option>
                  <option value="closed">Closed Shifts</option>
                </TextField>
              </>
            )}
            {isSalesReport && reportId !== 'customer-ledger' && reportId !== 'sales-return' && (
              <TextField
                select
                size="small"
                label="Mode"
                value={salesMode}
                onChange={(e) => setSalesMode(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 140 } }}
              >
                <option value="ALL">All Modes</option>
                <option value="cash">Cash</option>
                <option value="credit">Credit</option>
              </TextField>
            )}
            {isSalesReport && reportId !== 'customer-ledger' && reportId !== 'sales-by-salesperson' && (
              <TextField
                select
                size="small"
                label="Salesperson"
                value={salesPersonId}
                onChange={(e) => setSalesPersonId(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 180, md: 220 } }}
              >
                <option value="ALL">All Salespersons</option>
                {usersList.map((u) => {
                  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'User';
                  return (
                    <option key={u.id} value={u.id}>
                      {name}
                    </option>
                  );
                })}
              </TextField>
            )}
            {reportId === 'activity-log-report' && (
              <>
                <TextField
                  select
                  size="small"
                  label="Filter by User"
                  value={activityUserId}
                  onChange={(e) => setActivityUserId(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: '100%', sm: 190 } }}
                >
                  <option value="ALL">All Users</option>
                  {usersList.map((u) => {
                    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || u.email || 'User';
                    return (
                      <option key={u.id} value={u.id}>
                        {name}
                      </option>
                    );
                  })}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Action Type"
                  value={activityAction}
                  onChange={(e) => setActivityAction(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                >
                  <option value="ALL">All Actions</option>
                  <option value="LOGIN">LOGIN</option>
                  <option value="CREATE">CREATE</option>
                  <option value="UPDATE">UPDATE</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PAYMENT">PAYMENT</option>
                  <option value="OPEN_SHIFT">OPEN SHIFT</option>
                  <option value="CLOSE_SHIFT">CLOSE SHIFT</option>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Module / Resource"
                  value={activityResource}
                  onChange={(e) => setActivityResource(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: '100%', sm: 190 } }}
                >
                  <option value="ALL">All Modules</option>
                  <option value="sales-order">Sales & Invoices</option>
                  <option value="purchase-order">Purchases</option>
                  <option value="product">Products</option>
                  <option value="customer">Customers</option>
                  <option value="user">Users</option>
                  <option value="auth">Authentication</option>
                  <option value="register-session">POS Shifts</option>
                </TextField>
              </>
            )}
            {reportId === 'customer-ledger' && (
              <Autocomplete
                size="small"
                options={customersList}
                loading={isCustomersLoading}
                getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}${option.phone ? ` - ${option.phone}` : ''}`}
                value={customersList.find((c) => c.id === customerId) || null}
                onChange={(event, newValue) => {
                  setCustomerId(newValue ? newValue.id : null);
                  if (newValue) {
                    setSearchParams({ customerId: newValue.id });
                  } else {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('customerId');
                    setSearchParams(newParams);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="All Customers (or Select Specific)"
                    sx={{ width: { xs: '100%', sm: 280, md: 320 } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isCustomersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {reportId === 'supplier-ledger' && (
              <Autocomplete
                size="small"
                options={suppliersList}
                loading={isSuppliersLoading}
                getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}${option.phone ? ` - ${option.phone}` : ''}`}
                value={suppliersList.find((s) => s.id === supplierId) || null}
                onChange={(event, newValue) => {
                  setSupplierId(newValue ? newValue.id : null);
                  if (newValue) {
                    setSearchParams({ supplierId: newValue.id });
                  } else {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('supplierId');
                    setSearchParams(newParams);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="All Suppliers (or Select Specific)"
                    sx={{ width: { xs: '100%', sm: 280, md: 320 } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSuppliersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {reportId === 'bill-wise-profit' && (
              <TextField
                select
                size="small"
                label="Filter Bills"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: { xs: '100%', sm: 180 } }}
              >
                <option value="ALL">All Bills</option>
                <option value="PROFIT">Profitable Only</option>
                <option value="LOSS">Loss-Making Only</option>
                <option value="CASH">Cash Bills</option>
                <option value="CREDIT">Credit Bills</option>
                <option value="RETURN">Returns Only</option>
              </TextField>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPdf}
                disabled={!data}
                sx={{
                  flex: { xs: '1 1 calc(50% - 4px)', sm: 'none' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 1 },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<TableChartIcon />}
                onClick={handleExportExcel}
                disabled={!data}
                sx={{
                  flex: { xs: '1 1 calc(50% - 4px)', sm: 'none' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 1 },
                }}
              >
                Export Excel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading report…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          {error?.data?.message || 'Failed to load report'}
        </Alert>
      )}

      {data && !isLoading && (
        <>
          {/* Report period (print) */}
          <Box sx={{ mb: 2, '@media print': { mb: 1 } }}>
            <Typography variant="body2" color="text.secondary">
              Period: {formatDate(fromDate)} – {formatDate(toDate)}
            </Typography>
          </Box>

          {/* Summary */}
          {summaryItems.length > 0 && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <SummarizeOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Summary
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fill, minmax(200px, 1fr))' }, gap: { xs: 1.25, sm: 2 } }}>
                  {summaryItems.map((item, index) => {
                    const isAudit = item.label === 'Audit Status';
                    const isDiscrepancy = item.label === 'Discrepancy';
                    const isClosing = item.label.toLowerCase().includes('closing');
                    const isOpening = item.label.toLowerCase().includes('opening');
                    const isSales = item.label.toLowerCase().includes('sales');
                    const isReceipt = item.label.toLowerCase().includes('receipt');

                    let statusColor = 'primary.main';
                    let bgColor = alpha(theme.palette.primary.main, 0.04);
                    let borderColor = alpha(theme.palette.primary.main, 0.1);

                    if (isAudit) {
                      statusColor = item.value === 'MATCHED' ? 'success.main' : 'error.main';
                      bgColor = alpha(theme.palette[item.value === 'MATCHED' ? 'success' : 'error'].main, 0.04);
                      borderColor = alpha(theme.palette[item.value === 'MATCHED' ? 'success' : 'error'].main, 0.1);
                    } else if (isDiscrepancy && Number(item.value) !== 0) {
                      statusColor = 'warning.main';
                      bgColor = alpha(theme.palette.warning.main, 0.04);
                      borderColor = alpha(theme.palette.warning.main, 0.1);
                    } else if (isClosing || isOpening) {
                       statusColor = 'secondary.main';
                       bgColor = alpha(theme.palette.secondary.main, 0.04);
                       borderColor = alpha(theme.palette.secondary.main, 0.1);
                    } else if (isSales || isReceipt) {
                       statusColor = 'success.main';
                       bgColor = alpha(theme.palette.success.main, 0.04);
                       borderColor = alpha(theme.palette.success.main, 0.1);
                    }

                    return (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          p: { xs: 1.5, sm: 2.5 },
                          borderRadius: 3,
                          bgcolor: bgColor,
                          border: '1px solid',
                          borderColor: borderColor,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                            borderColor: statusColor,
                          }
                        }}
                      >
                        <Box sx={{
                          position: 'absolute',
                          top: -10, right: -10,
                          width: 60, height: 60,
                          bgcolor: statusColor,
                          opacity: 0.05,
                          borderRadius: '50%',
                          transition: 'transform 0.3s ease',
                          '.MuiBox-root:hover > &': {
                            transform: 'scale(1.5)'
                          }
                        }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5, position: 'relative', zIndex: 1 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h6" sx={{ color: statusColor, fontWeight: 800, lineHeight: 1.2, position: 'relative', zIndex: 1, letterSpacing: '-0.02em' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {data.message && (
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {data.message}
            </Alert>
          )}

          {/* Table */}
          {reportId === 'single-day-activity' && data.rows && data.rows.length > 0 ? (
            <Box className="no-print" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fill, minmax(320px, 1fr))' }, gap: 4, mb: 5 }}>
              {data.rows.map((row, idx) => {
                const activity = row.activity;
                const amount = row.amount;
                let Icon = PointOfSaleIcon;
                let colorKey = 'primary';

                if (activity.includes('Total Sales') && !activity.includes('Returns')) {
                  Icon = PointOfSaleIcon; colorKey = 'primary';
                } else if (activity.includes('Cash Sales')) {
                  Icon = AttachMoneyIcon; colorKey = 'success';
                } else if (activity.includes('Card Sales')) {
                  Icon = CreditCardIcon; colorKey = 'info';
                } else if (activity.includes('Bank Sales')) {
                  Icon = AccountBalanceIcon; colorKey = 'info';
                } else if (activity.includes('Sales Returns')) {
                  Icon = KeyboardReturnIcon; colorKey = 'warning';
                } else if (activity.includes('Discounts')) {
                  Icon = LocalOfferIcon; colorKey = 'secondary';
                } else if (activity.includes('Total Purchases') && !activity.includes('Returns')) {
                  Icon = ShoppingCartIcon; colorKey = 'primary';
                } else if (activity.includes('Purchase Returns')) {
                  Icon = KeyboardReturnIcon; colorKey = 'warning';
                } else if (activity.includes('Expenses')) {
                  Icon = ReceiptIcon; colorKey = 'error';
                } else if (activity.includes('Balance in Hand')) {
                  Icon = AccountBalanceWalletIcon; colorKey = 'success';
                }

                const color = theme.palette[colorKey].main;
                const colorLight = theme.palette[colorKey].light;
                const colorDark = theme.palette[colorKey].dark;

                return (
                  <Card key={idx} elevation={0} sx={{ 
                    borderRadius: '24px', 
                    border: '1px solid',
                    borderColor: alpha(color, 0.15),
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
                    backdropFilter: 'blur(20px)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    boxShadow: `0 10px 40px -10px ${alpha(color, 0.15)}`,
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: `0 24px 48px -12px ${alpha(color, 0.3)}`,
                      borderColor: alpha(color, 0.5),
                    }
                  }}>
                    {/* Glowing mesh background */}
                    <Box sx={{
                      position: 'absolute',
                      top: '-20%',
                      right: '-10%',
                      width: '70%',
                      height: '70%',
                      background: `radial-gradient(circle, ${alpha(colorLight, 0.3)} 0%, transparent 70%)`,
                      filter: 'blur(40px)',
                      zIndex: 0,
                      transition: 'all 0.6s ease',
                      '.MuiCard-root:hover &': {
                        transform: 'scale(1.2) translate(-10%, 10%)',
                        background: `radial-gradient(circle, ${alpha(colorLight, 0.5)} 0%, transparent 70%)`,
                      }
                    }} />
                    
                    {/* Decorative Bottom Bar */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      height: '4px',
                      width: '100%',
                      background: `linear-gradient(90deg, ${colorDark} 0%, ${colorLight} 100%)`,
                      opacity: 0.7,
                      transition: 'opacity 0.3s, height 0.3s',
                      '.MuiCard-root:hover &': {
                        opacity: 1,
                        height: '6px'
                      }
                    }} />

                    <CardContent sx={{ position: 'relative', zIndex: 1, p: 4, pb: '36px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ 
                          color: 'text.secondary', 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.5,
                          fontSize: '0.8rem'
                        }}>
                          {activity}
                        </Typography>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: '14px', 
                          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.25)} 100%)`, 
                          color: color, 
                          display: 'flex',
                          boxShadow: `inset 0 0 0 1px ${alpha(color, 0.2)}`
                        }}>
                          <Icon fontSize="medium" />
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ 
                        color: 'text.primary', 
                        fontWeight: 800, 
                        letterSpacing: '-0.02em',
                      }}>
                        {formatAmount(amount)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : data.rows && data.rows.length > 0 && data.columns && data.columns.length > 0 && (() => {
            const normalizedColumns = data.columns.map((col, i) =>
              typeof col === 'string'
                ? { id: col, label: col }
                : { id: col.id ?? col.label ?? `col_${i}`, label: col.label ?? col.id ?? `Column ${i + 1}` }
            );

            const columnTotals = {};
            normalizedColumns.forEach((col, colIdx) => {
              const colLower = col.id.toLowerCase();
              const isNumeric = data.rows.some(row => typeof row[col.id] === 'number');
              
              if (colLower === 'balance' && data.summary?.closingBalance != null) {
                columnTotals[col.id] = data.summary.closingBalance;
              } else if (isNumeric) {
                const sum = data.rows.reduce((acc, row) => {
                  if ((reportId === 'customer-ledger' || reportId === 'supplier-ledger') && row.type === 'Opening Balance') {
                    return acc;
                  }
                  const val = Number(row[col.id]);
                  return acc + (Number.isFinite(val) ? val : 0);
                }, 0);
                columnTotals[col.id] = sum;
              } else {
                columnTotals[col.id] = null;
              }
            });

            return (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'auto',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {normalizedColumns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.grey[500], 0.08),
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((row, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          bgcolor: idx % 2 === 1 ? alpha(theme.palette.grey[500], 0.03) : 'transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        {normalizedColumns.map((col) => {
                          const raw = row[col.id];
                          let display = raw;
                          const colLower = col.id.toLowerCase();
                          const isQuantity =
                            isInventoryReport ||
                            (typeof raw === 'number' &&
                              (colLower.includes('quantity') ||
                                colLower.includes('qty') ||
                                colLower === 'totalquantity' ||
                                colLower === 'quantitysold' ||
                                colLower === 'totalsold' ||
                                colLower === 'inqty' ||
                                colLower === 'outqty'));
                          const isCurrency =
                            !isInventoryReport &&
                            typeof raw === 'number' &&
                            !isQuantity &&
                            (colLower.includes('amount') ||
                              colLower.includes('value') ||
                              colLower.includes('revenue') ||
                              colLower.includes('cost') ||
                              colLower === 'profit' ||
                              colLower === 'debit' ||
                              colLower === 'credit' ||
                              colLower === 'balance' ||
                              colLower === 'subtotal' ||
                              (colLower.includes('total') && colLower !== 'totalrows'));
                          if (isCurrency) {
                            display = formatAmount(raw);
                          } else if (typeof raw === 'number' && (colLower.includes('margin') || colLower === 'vatrate')) {
                            display = `${Number(raw).toFixed(1)}%`;
                          } else if (
                            raw &&
                            (colLower.includes('time') || colLower === 'openedat' || colLower === 'closedat')
                          ) {
                            display = formatDateTime(raw);
                          } else if (
                            raw &&
                            (colLower.includes('date') || col.id === 'date')
                          ) {
                            display = formatDate(raw);
                          }
                          if (colLower === 'type' || colLower === 'action') {
                            const valStr = String(raw).toUpperCase();
                            let chipColor = 'primary';
                            if (valStr.includes('DELETE') || valStr.includes('CANCEL') || valStr.includes('REMOVE')) chipColor = 'error';
                            else if (valStr.includes('CREATE') || valStr.includes('ADD') || valStr.includes('OPEN_SHIFT')) chipColor = 'success';
                            else if (valStr.includes('UPDATE') || valStr.includes('EDIT')) chipColor = 'info';
                            else if (valStr.includes('RETURN') || valStr.includes('CLOSE_SHIFT')) chipColor = 'warning';
                            else if (valStr.includes('PAYMENT')) chipColor = 'secondary';
                            else if (valStr.includes('LOGIN')) chipColor = 'primary';
                            return (
                              <TableCell key={col.id} sx={{ py: 1, borderColor: 'divider' }}>
                                {raw ? (
                                  <Chip label={String(raw)} size="small" color={chipColor} variant="filled" sx={{ fontWeight: 700, borderRadius: 1.5 }} />
                                ) : '–'}
                              </TableCell>
                            );
                          }
                          if (colLower === 'status' || colLower === 'auditstatus') {
                            let chipColor = 'default';
                            const valStr = String(raw).toUpperCase();
                            if (valStr.includes('MATCHED') || valStr.includes('DELIVERED') || valStr.includes('PAID') || valStr.includes('POSTED') || valStr === 'CLOSED') chipColor = 'success';
                            else if (valStr.includes('OPEN') || valStr.includes('ACTIVE')) chipColor = 'info';
                            else if (valStr.includes('POSITIVE') || valStr.includes('CONFIRMED')) chipColor = 'info';
                            else if (valStr.includes('NEGATIVE') || valStr.includes('CANCELLED')) chipColor = 'error';
                            else if (valStr.includes('DISCREPANCY') || valStr.includes('HOLD') || valStr.includes('DRAFT')) chipColor = 'warning';
                            else chipColor = 'primary';
                            
                            return (
                              <TableCell key={col.id} sx={{ py: 1, borderColor: 'divider' }}>
                                {raw ? (
                                  <Chip label={String(raw)} size="small" color={chipColor} variant="filled" sx={{ fontWeight: 700, borderRadius: 1.5 }} />
                                ) : '–'}
                              </TableCell>
                            );
                          }

                          let cellColor = undefined;
                          let cellFontWeight = isCurrency || colLower.includes('value') || colLower.includes('margin') ? 600 : 400;

                          if (colLower === 'profit' || colLower.includes('margin') || colLower === 'variance' || colLower.includes('diff')) {
                            cellFontWeight = 700;
                            const num = Number(raw);
                            if (!Number.isNaN(num)) {
                              if (num > 0 && (colLower === 'profit' || colLower.includes('margin'))) cellColor = theme.palette.success.main;
                              else if (num < 0) cellColor = theme.palette.error.main;
                              else if (num > 0) cellColor = theme.palette.info.main;
                            }
                          }

                          return (
                            <TableCell
                              key={col.id}
                              sx={{
                                py: 1.5,
                                fontWeight: cellFontWeight,
                                color: cellColor,
                                borderColor: 'divider',
                              }}
                            >
                              {display != null && display !== '' ? String(display) : '–'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}

                    {/* Total Row */}
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      {normalizedColumns.map((col, colIdx) => {
                        const totalVal = columnTotals[col.id];
                        const colLower = col.id.toLowerCase();
                        
                        let display = '–';
                        if (totalVal !== null) {
                          const isQuantity =
                            isInventoryReport ||
                            colLower.includes('quantity') ||
                            colLower.includes('qty') ||
                            colLower === 'totalquantity' ||
                            colLower === 'quantitysold' ||
                            colLower === 'totalsold' ||
                            colLower === 'inqty' ||
                            colLower === 'outqty';
                          
                          const isPercentage = colLower.includes('margin') || colLower === 'vatrate';

                          if (isQuantity) {
                            display = Number(totalVal).toLocaleString();
                          } else if (isPercentage) {
                            display = '–';
                          } else {
                            display = formatAmount(totalVal);
                          }
                        } else if (colIdx === 0) {
                          display = 'Total';
                        }

                        return (
                          <TableCell
                            key={`total-${col.id}`}
                            sx={{
                              py: 1.5,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: 'text.primary',
                              borderTop: '2px solid',
                              borderColor: theme.palette.primary.main,
                              borderBottom: '2px solid',
                            }}
                          >
                            {display}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}

          {data.rows && data.rows.length === 0 && !data.message && (
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                py: 6,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No data for the selected period.
              </Typography>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

export default ReportView;
